/**
 * /admin/diag — Panel admin que lista los errores reportados por el
 * ErrorBoundary del frontend (colección `frontend_errors`).
 *
 * - Polling cada 60s
 * - Marca todos como leídos al entrar (limpia badge sidebar)
 * - Expand stacktrace + component_stack on click
 * - Eliminar individual + Vaciar todo
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertTriangle, RefreshCw, Trash2, ChevronDown, ChevronRight, ExternalLink, Search, BarChart3, Users as UsersIcon, ShieldCheck } from "lucide-react";
import { DiagnosticoRutabilidadDialog } from "@/components/DiagnosticoRutabilidad";
import { toast } from "sonner";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function relativeTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return "hace unos segundos";
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  return `hace ${Math.floor(diff / 86400)} días`;
}

export default function AdminDiag() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/diag/frontend-errors", { params: { limit: 200 } });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cargar");
    } finally {
      setLoading(false);
    }
  };

  // Cargar + marcar todos como leídos
  useEffect(() => {
    load();
    api.post("/diag/frontend-errors/mark-read").catch(() => {});
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, []);

  const toggle = (id) => {
    setExpanded((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const eliminar = async (eid) => {
    if (!window.confirm("¿Eliminar este error del registro?")) return;
    try {
      await api.delete(`/diag/frontend-errors/${eid}`);
      toast.success("Eliminado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar");
    }
  };

  const vaciar = async () => {
    if (!window.confirm(`¿Vaciar TODOS los errores (${items.length})? Esta acción no se puede deshacer.`)) return;
    try {
      const { data } = await api.delete("/diag/frontend-errors");
      toast.success(`${data.deleted} errores borrados`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const filtered = useMemo(() => {
    if (!q) return items;
    const lc = q.toLowerCase();
    return items.filter((e) =>
      (e.message || "").toLowerCase().includes(lc) ||
      (e.url || "").toLowerCase().includes(lc) ||
      (e.user_email || "").toLowerCase().includes(lc)
    );
  }, [items, q]);

  // Agrupar por mensaje para detectar errores recurrentes
  const grouped = useMemo(() => {
    const byMsg = {};
    for (const e of filtered) {
      const key = e.message || "(sin mensaje)";
      if (!byMsg[key]) byMsg[key] = { message: key, count: 0, items: [], last: null, users: new Set() };
      byMsg[key].count += 1;
      byMsg[key].items.push(e);
      if (!byMsg[key].last || e.created_at > byMsg[key].last) byMsg[key].last = e.created_at;
      if (e.user_email) byMsg[key].users.add(e.user_email);
    }
    return Object.values(byMsg).sort((a, b) => b.last.localeCompare(a.last));
  }, [filtered]);

  return (
    <div className="p-6 max-w-[1800px]" data-testid="admin-diag-page">
      <PageHeader
        title="Diagnóstico · Errores frontend"
        subtitle="Crashes capturados por el ErrorBoundary. Te llegan automáticamente para que sepas qué está fallando antes que tus usuarios."
        action={
          <div className="flex items-center gap-2">
            <Link to="/admin/diag/stats">
              <Button variant="outline" data-testid="open-stats" className="bg-transparent border-amber-700/40 text-amber-300 hover:bg-amber-500/10">
                <BarChart3 className="w-4 h-4 mr-1.5" /> Estadísticas
              </Button>
            </Link>
            <Button variant="outline" onClick={load} data-testid="diag-refresh" className="bg-transparent border-zinc-700 text-zinc-300">
              <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refrescar
            </Button>
            {items.length > 0 && (
              <Button variant="outline" onClick={vaciar} data-testid="diag-clear-all" className="bg-transparent border-red-900/40 text-red-400 hover:bg-red-500/10">
                <Trash2 className="w-4 h-4 mr-1.5" /> Vaciar todo
              </Button>
            )}
          </div>
        }
      />

      <div className="flex items-center gap-3 mb-4 mt-4">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            data-testid="diag-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por mensaje, URL o usuario…"
            className="bg-zinc-950/40 border-zinc-800 pl-9 text-zinc-200"
          />
        </div>
        <div className="text-xs text-zinc-500">
          {filtered.length} reporte{filtered.length === 1 ? "" : "s"} · {grouped.length} error{grouped.length === 1 ? "" : "es"} únicos
        </div>
      </div>

      {/* Panel de mantenimiento: limpieza de users externos huérfanos */}
      <ExternosHuerfanosPanel />

      {/* Panel diagnóstico: por qué una petición no aparece en rutas */}
      <DiagPorQueNoEnrutarPanel />
      <IncoherenciasPanel />

      {!loading && filtered.length === 0 && (
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-emerald-400" />
          </div>
          <div className="text-lg font-semibold text-zinc-200">Todo limpio 🎉</div>
          <div className="text-sm text-zinc-500 mt-1">No hay errores reportados. Cuando un usuario tenga un crash en el frontend, aparecerá aquí automáticamente.</div>
        </div>
      )}

      <div className="space-y-3" data-testid="diag-errors-list">
        {grouped.map((g) => {
          const isOpen = expanded.has(g.message);
          const recent = g.items[0];
          return (
            <div key={g.message}
              className="bg-zinc-900/40 border border-amber-500/20 rounded-xl overflow-hidden"
              data-testid="diag-error-group"
            >
              <button
                type="button"
                onClick={() => toggle(g.message)}
                className="w-full p-4 text-left flex items-start gap-3 hover:bg-amber-500/5 transition-colors"
                data-testid="diag-error-group-toggle"
              >
                <div className="w-8 h-8 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center shrink-0 mt-0.5">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-red-500/15 text-red-400 font-mono">
                      ×{g.count}
                    </span>
                    <span className="text-xs text-zinc-500">{relativeTime(g.last)}</span>
                    {g.users.size > 0 && (
                      <span className="text-xs text-zinc-500">·  {g.users.size} usuario{g.users.size === 1 ? "" : "s"}</span>
                    )}
                  </div>
                  <div className="font-mono text-sm text-zinc-200 break-words">{g.message}</div>
                  {recent?.url && (
                    <div className="text-xs text-zinc-500 mt-1 flex items-center gap-1 truncate">
                      <ExternalLink className="w-3 h-3 shrink-0" /> {recent.url}
                    </div>
                  )}
                </div>
                {isOpen ? <ChevronDown className="w-4 h-4 text-zinc-500 mt-1.5" /> : <ChevronRight className="w-4 h-4 text-zinc-500 mt-1.5" />}
              </button>

              {isOpen && (
                <div className="border-t border-zinc-800/60 divide-y divide-zinc-800/40">
                  {g.items.map((e) => (
                    <div key={e.id} className="p-4 bg-zinc-950/40">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="text-xs text-zinc-400">
                          <strong className="text-zinc-300">{e.user_email || "anónimo"}</strong>
                          <span className="text-zinc-600"> · </span>
                          {fmtDate(e.created_at)}
                          {e.url && (
                            <>
                              <span className="text-zinc-600"> · </span>
                              <a href={e.url} target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:underline">
                                {e.url.replace(/^https?:\/\/[^/]+/, "")}
                              </a>
                            </>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => eliminar(e.id)}
                          data-testid="diag-error-delete"
                          className="text-zinc-500 hover:text-red-400 h-7 text-xs"
                        >
                          <Trash2 className="w-3 h-3 mr-1" /> Borrar
                        </Button>
                      </div>
                      {e.stack && (
                        <details className="text-xs font-mono text-zinc-500 bg-zinc-950/80 border border-zinc-800 rounded p-2 mb-2">
                          <summary className="cursor-pointer text-zinc-400 hover:text-zinc-200">Stack trace</summary>
                          <pre className="whitespace-pre-wrap break-words mt-2 text-zinc-500">{e.stack}</pre>
                        </details>
                      )}
                      {e.component_stack && (
                        <details className="text-xs font-mono text-zinc-500 bg-zinc-950/80 border border-zinc-800 rounded p-2 mb-2">
                          <summary className="cursor-pointer text-zinc-400 hover:text-zinc-200">Component stack</summary>
                          <pre className="whitespace-pre-wrap break-words mt-2 text-zinc-500">{e.component_stack}</pre>
                        </details>
                      )}
                      <div className="text-[10px] text-zinc-600 font-mono truncate" title={e.user_agent}>
                        {e.user_agent}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


/**
 * Panel compacto: detecta y limpia "users huérfanos" — usuarios externos
 * autocreados al subir nóminas que quedaron desconectados de su colaborador
 * (no enlazados, sin SS/teléfono/carnet). Muestran inactivos y vacíos en la
 * lista de Equipo. El fix iter51 previene su creación futura; este panel
 * limpia los existentes (creados antes del fix).
 */
function ExternosHuerfanosPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/diag/externos-huerfanos");
      setData(r.data);
      setOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al detectar");
    } finally {
      setLoading(false);
    }
  };

  const limpiar = async () => {
    if (!data) return;
    const n = data.vacios_seguros_borrar?.length || 0;
    if (n === 0) {
      toast.info("No hay huérfanos vacíos para borrar");
      return;
    }
    if (!window.confirm(`Vas a BORRAR ${n} user(s) externo(s) huérfano(s) vacío(s). Esta acción no se puede deshacer (pero estos users no tienen datos relevantes). ¿Continuar?`)) return;
    setLoading(true);
    try {
      const r = await api.post("/admin/diag/externos-huerfanos/cleanup");
      toast.success(`${r.data.borrados} user(s) borrado(s)`);
      await cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al limpiar");
    } finally {
      setLoading(false);
    }
  };

  const total = data ? (data.total_huerfanos || 0) : null;

  return (
    <div className="bg-zinc-900/40 border border-violet-500/20 rounded-xl p-4 mb-4" data-testid="externos-huerfanos-panel">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-violet-500/15 border border-violet-500/30 flex items-center justify-center shrink-0">
          <UsersIcon className="w-4 h-4 text-violet-300" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-zinc-100">
            Limpieza · Users externos huérfanos
          </div>
          <div className="text-xs text-zinc-500 mt-0.5">
            Detecta y elimina users autocreados por el subidor de nóminas que quedaron desconectados (aparecen en Equipo como "inactivos sin datos").
          </div>
          {data && (
            <div className="text-xs mt-2 flex flex-wrap gap-3">
              <span className={`px-2 py-0.5 rounded font-mono ${total === 0 ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>
                Total huérfanos: {total}
              </span>
              <span className="text-zinc-400">Vacíos (seguros): <b className="text-zinc-200">{data.vacios_seguros_borrar?.length || 0}</b></span>
              <span className="text-zinc-400">Con datos (revisar): <b className="text-zinc-200">{data.con_datos_revisar?.length || 0}</b></span>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1.5 shrink-0">
          <Button size="sm" variant="outline" onClick={cargar} disabled={loading}
            className="bg-transparent border-violet-700/40 text-violet-300 hover:bg-violet-500/10 h-7 text-xs"
            data-testid="btn-detectar-huerfanos">
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} /> Detectar
          </Button>
          {data && (data.vacios_seguros_borrar?.length || 0) > 0 && (
            <Button size="sm" onClick={limpiar} disabled={loading}
              className="bg-violet-500 text-white hover:bg-violet-400 h-7 text-xs"
              data-testid="btn-limpiar-huerfanos">
              <ShieldCheck className="w-3 h-3 mr-1" /> Limpiar {data.vacios_seguros_borrar.length}
            </Button>
          )}
        </div>
      </div>

      {open && data && (data.vacios_seguros_borrar?.length > 0 || data.con_datos_revisar?.length > 0) && (
        <div className="mt-3 space-y-2 text-xs">
          {data.vacios_seguros_borrar?.length > 0 && (
            <details className="bg-zinc-950/40 border border-zinc-800 rounded p-2" open>
              <summary className="cursor-pointer text-emerald-300">
                {data.vacios_seguros_borrar.length} huérfano(s) vacío(s) — seguros de borrar
              </summary>
              <ul className="mt-2 space-y-1 text-zinc-400 font-mono">
                {data.vacios_seguros_borrar.map((h) => (
                  <li key={h.user_id}>
                    · <b className="text-zinc-200">{h.name}</b> · {h.email}
                    {h.colaborador_nombre && <span className="text-zinc-500"> (colab: {h.colaborador_nombre})</span>}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {data.con_datos_revisar?.length > 0 && (
            <details className="bg-zinc-950/40 border border-amber-700/40 rounded p-2">
              <summary className="cursor-pointer text-amber-300">
                ⚠ {data.con_datos_revisar.length} huérfano(s) con datos — revisar manualmente
              </summary>
              <ul className="mt-2 space-y-1 text-zinc-400 font-mono">
                {data.con_datos_revisar.map((h) => (
                  <li key={h.user_id}>
                    · <b className="text-zinc-200">{h.name}</b> · {h.email} · active={String(h.active)}
                    {h.colaborador_nombre && <span className="text-zinc-500"> (colab: {h.colaborador_nombre})</span>}
                  </li>
                ))}
              </ul>
              <div className="mt-2 text-[10px] text-zinc-500">
                Estos users tienen datos (SS, teléfono o carnet). Decide tú: si son los buenos, ve a Equipo y actualízalos. Si son duplicados que ya copiaste a otro user, bórralos manualmente desde Equipo.
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}



/**
 * Panel diagnóstico: "¿Por qué la petición XXX no aparece en planificación
 * de rutas?". Llama a `/admin/diag/por-que-no-enrutar/{codigo}` y muestra
 * el veredicto humano-legible.
 */
function DiagPorQueNoEnrutarPanel() {
  const [codigo, setCodigo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const buscar = async () => {
    if (!codigo.trim()) return;
    setLoading(true);
    setData(null);
    try {
      const r = await api.get(`/admin/diag/por-que-no-enrutar/${codigo.trim()}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al diagnosticar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-zinc-900/40 border border-sky-500/20 rounded-xl p-4 mb-4" data-testid="diag-no-enrutar">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-sky-500/15 border border-sky-500/30 flex items-center justify-center shrink-0">
          <BarChart3 className="w-4 h-4 text-sky-300" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-zinc-100">
            Diagnóstico · ¿Por qué una petición no aparece en rutas?
          </div>
          <div className="text-xs text-zinc-500 mt-0.5">
            Introduce el código (ej: <code className="text-amber-300">DNF-2026-0262</code>) y dime qué filtro la está tumbando.
          </div>
          <div className="flex items-center gap-2 mt-2">
            <Input value={codigo}
              onChange={(e) => setCodigo(e.target.value.toUpperCase().trim())}
              onKeyDown={(e) => e.key === "Enter" && buscar()}
              placeholder="DNF-2026-XXXX"
              className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-xs font-mono"
              data-testid="diag-no-enrutar-input" />
            <Button size="sm" onClick={buscar} disabled={loading || !codigo}
              className="bg-sky-500 text-white hover:bg-sky-400 h-9"
              data-testid="diag-no-enrutar-btn">
              {loading ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Search className="w-3.5 h-3.5 mr-1" />}
              Diagnosticar
            </Button>
          </div>
        </div>
      </div>

      {data && (
        <div className="mt-3 space-y-2 text-xs">
          {/* Resumen petición */}
          <div className="bg-zinc-950/40 border border-zinc-800 rounded p-2">
            <div className="text-zinc-100 font-semibold">
              {data.peticion.codigo} · estado: <span className={data.checks_peticion.estado_peticion_ok ? "text-emerald-300" : "text-red-300"}>{data.peticion.estado}</span>
            </div>
            {!data.checks_peticion.estado_peticion_ok && (
              <div className="text-red-300 mt-1">
                ⚠ La petición está en estado <code>{data.peticion.estado}</code>. Para enrutar debe estar en uno de:
                <code className="text-amber-300 ml-1">{(data.checks_peticion.estados_permitidos || []).join(", ")}</code>
              </div>
            )}
          </div>

          {/* Líneas */}
          {data.lineas?.map((ln) => (
            <div key={ln.linea_id}
              className={`rounded p-2 border ${ln.aparece_disponible ? "border-emerald-700/40 bg-emerald-500/5" : "border-red-700/40 bg-red-500/5"}`}>
              <div className="flex items-center justify-between">
                <div className="text-zinc-200">
                  {ln.aparece_disponible ? "✅" : "❌"} Línea: <span className="font-mono text-[10px]">{ln.linea_id.slice(0, 8)}</span>
                  {" · "}<b>{ln.linea_descripcion || "(sin descripción)"}</b>
                  <span className="ml-2 text-[10px] text-zinc-500">tipo={ln.tipo_linea} · estado={ln.estado_linea}</span>
                </div>
              </div>
              {ln.razones_NO_aparece?.length > 0 && (
                <ul className="mt-1 ml-5 list-disc text-red-300">
                  {ln.razones_NO_aparece.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              )}
              {ln.componentes?.length > 0 && (
                <details className="mt-1">
                  <summary className="text-zinc-500 cursor-pointer">Componentes ({ln.componentes.length})</summary>
                  <ul className="ml-4 mt-1 space-y-0.5">
                    {ln.componentes.map((c) => (
                      <li key={c.id} className="text-zinc-400 font-mono">
                        {c.listo_para_ruta ? "✓" : "✗"} {c.nombre} · origen=<b>{c.origen}</b> · estado=<b>{c.estado}</b>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              {ln.ruta_que_la_contiene && (
                <div className="mt-1 text-amber-300 text-[11px]">
                  🚚 Ya está en la ruta <b>"{ln.ruta_que_la_contiene.ruta_nombre}"</b>
                  {" · "}fecha {ln.ruta_que_la_contiene.ruta_fecha}
                  {" · "}estado <code>{ln.ruta_que_la_contiene.ruta_estado}</code>
                </div>
              )}

              {/* Datos UI: si aparece_disponible:true pero el usuario dice que no la ve,
                  es por un filtro frontend. Mostramos los valores que la UI usaría. */}
              {ln.aparece_disponible && ln.datos_ui && (
                <div className="mt-2 p-2 rounded bg-sky-500/10 border border-sky-500/30 text-[11px]">
                  <div className="text-sky-200 font-semibold mb-1">
                    ⚠ El backend SÍ devuelve esta línea como disponible. Si no la ves en la pantalla "Planificación de Rutas", es por un filtro de la UI.
                  </div>
                  <div className="text-zinc-300 grid grid-cols-2 gap-x-3 gap-y-0.5 font-mono">
                    <div>📍 Provincia: <b className="text-sky-200">{ln.datos_ui.provincia || "—"}</b></div>
                    <div>🏙 Ciudad: <b className="text-sky-200">{ln.datos_ui.ciudad || "—"}</b></div>
                    <div>🏢 Espacio: <b className="text-sky-200">{ln.datos_ui.espacio_nombre || "—"}</b></div>
                    <div>👤 Cliente: <b className="text-sky-200">{ln.datos_ui.cliente_nombre || "—"}</b></div>
                    <div>📅 Fecha objetivo: <b className="text-sky-200">{(ln.datos_ui.fecha_objetivo || "").slice(0, 10) || "—"}</b></div>
                    <div>📦 Material listo desde: <b className="text-sky-200">{ln.datos_ui.material_listo_desde || "—"}</b></div>
                  </div>
                  <div className="text-zinc-400 mt-1.5 text-[10px]">
                    Comprueba en la pantalla de Planificación de Rutas si tienes activos:
                    <b className="text-amber-300"> filtro de provincia</b> (debe coincidir o ser "todas"),
                    <b className="text-amber-300"> rango de fechas</b> (debe incluir la "fecha objetivo"),
                    <b className="text-amber-300"> "material listo hace &gt; N días"</b> (puede excluir esta línea si N es alto),
                    <b className="text-amber-300"> búsqueda por texto</b>.
                  </div>
                </div>
              )}
            </div>
          ))}

          {data.lineas?.length === 0 && (
            <div className="text-zinc-500">La petición no tiene líneas de trabajo.</div>
          )}
        </div>
      )}
    </div>
  );
}



// Oct-2026 · Detector read-only de estados internos incoherentes (caso DNF-2026-0463). NO corrige nada.
const INCOH_LABELS = {
  pendiente_con_bloqueo_decision: "Línea pendiente con bloqueo por NO realizada (zona muerta)",
  pendiente_con_agendada_en_ruta: "Línea pendiente que aún apunta a una ruta",
  componente_vinculado_a_ruta_inexistente: "Componente vinculado a ruta/parada inexistente",
  linea_no_finalizada_en_ruta_finalizada: "Línea no finalizada dentro de ruta finalizada (sin reset/liberar)",
  peticion_aprobada_con_no_realizada_pendiente: "Petición aprobada/cerrada con NO realizada pendiente de decisión",
};

function IncoherenciasPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [diag, setDiag] = useState(null);
  const cargar = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/diag/incoherencias"); setData(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail?.msg || e?.response?.data?.detail || "Error al detectar incoherencias"); }
    finally { setLoading(false); }
  };
  return (
    <div className="mt-8 rounded-xl border border-zinc-800 bg-zinc-900/40 p-4" data-testid="incoherencias-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-sm font-semibold text-zinc-100">🩺 Estados internos incoherentes</h2>
          <p className="text-xs text-zinc-500">Solo lectura: detecta líneas/componentes/peticiones con restos de rutas o NO realizadas. No corrige nada en masa.</p>
        </div>
        <button onClick={cargar} disabled={loading} data-testid="incoherencias-detectar" className="text-xs px-3 py-1.5 rounded bg-cyan-500/15 border border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/25 disabled:opacity-50">
          {loading ? "Analizando…" : "Detectar"}
        </button>
      </div>
      {data && (
        <div className="mt-3 space-y-3">
          <div className="text-xs text-zinc-300" data-testid="incoherencias-total">Total: <b className={data.total ? "text-amber-300" : "text-emerald-300"}>{data.total}</b></div>
          {Object.entries(data.tipos).map(([k, lst]) => (
            <details key={k} className="rounded border border-zinc-800 bg-zinc-950/40" open={lst.length > 0} data-testid={`incoh-${k}`}>
              <summary className="cursor-pointer px-3 py-2 text-xs flex items-center gap-2">
                <span className={`font-mono px-1.5 rounded ${lst.length ? "bg-amber-500/20 text-amber-200" : "bg-zinc-800 text-zinc-500"}`}>{lst.length}</span>
                <span className="text-zinc-200">{INCOH_LABELS[k] || k}</span>
              </summary>
              {lst.length > 0 && (
                <ul className="px-3 pb-2 space-y-1">
                  {lst.map((it, i) => (
                    <li key={i} className="text-[11px] text-zinc-400 flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-amber-300">{it.codigo || it.peticion_id?.slice(0, 8) || "—"}</span>
                      <span className="text-zinc-300">{it.descripcion || it.nombre || ""}</span>
                      {it.ruta_codigo && <span>· {it.ruta_codigo}{it.parada_orden != null ? ` #${it.parada_orden}` : ""}</span>}
                      {it.motivo && <span>· {it.motivo}</span>}
                      {it.estado_linea && <span>· línea {it.estado_linea}</span>}
                      {it.estado_peticion && <span>· petición {it.estado_peticion}</span>}
                      {(it.linea_id || it.peticion_id) && (
                        <button onClick={() => setDiag(it.linea_id ? { lineaId: it.linea_id } : { peticionId: it.peticion_id })} data-testid={`incoh-diag-${it.linea_id || it.peticion_id}`}
                          className="underline text-cyan-300 hover:text-cyan-200">Diagnóstico</button>
                      )}
                      {it.peticion_id && <a href={`/peticiones/${it.peticion_id}`} className="underline text-zinc-500 hover:text-zinc-300">abrir</a>}
                    </li>
                  ))}
                </ul>
              )}
            </details>
          ))}
        </div>
      )}
      {diag && <DiagnosticoRutabilidadDialog open onOpenChange={(o) => { if (!o) setDiag(null); }} peticionId={diag.peticionId} lineaId={diag.lineaId} />}
    </div>
  );
}
